import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminAxios, adminLogout } from "@/lib/adminAuth";

const AdminContext = createContext(null);
const LAST_SEEN_KEY = "bionet_admin_last_seen_total";

export const AdminProvider = ({ children }) => {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [chats, setChats] = useState([]);
  const [chatbotStats, setChatbotStats] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const lastKnownTotal = useRef(null);

  const computeUnread = useCallback((q, c) => {
    const total = q.length + c.length;
    const lastSeen = parseInt(localStorage.getItem(LAST_SEEN_KEY) || "0", 10);
    setUnreadCount(Math.max(0, total - lastSeen));
  }, []);

  const handle401 = useCallback(() => {
    adminLogout();
    nav("/admin/login");
  }, [nav]);

  const refreshAll = useCallback(async () => {
    try {
      const ax = adminAxios();
      const [s, q, c, ch, cs] = await Promise.all([
        ax.get("/admin/stats"),
        ax.get("/admin/quotes"),
        ax.get("/admin/contacts"),
        ax.get("/admin/chats"),
        ax.get("/admin/chatbot/stats"),
      ]);
      const newQuotes = (q.data.items || []).map((item) => ({
        ...item,
        status: item.status === "new" ? "nouveau" : (item.status || "nouveau"),
      }));
      const newContacts = c.data.items || [];
      setStats(s.data);
      setQuotes(newQuotes);
      setContacts(newContacts);
      setChats(ch.data.items || []);
      setChatbotStats(cs.data);
      lastKnownTotal.current = newQuotes.length + newContacts.length;
      computeUnread(newQuotes, newContacts);
    } catch (e) {
      if (e?.response?.status === 401) {
        handle401();
      } else {
        toast.error("Erreur de chargement des données.");
      }
    }
  }, [computeUnread, handle401]);

  const updateQuoteStatus = useCallback(async (id, status) => {
    try {
      await adminAxios().patch(`/admin/quotes/${id}/status`, { status });
      setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Statut mis à jour.");
    } catch {
      toast.error("Erreur lors de la mise à jour.");
    }
  }, []);

  const markAllRead = useCallback(() => {
    const total = lastKnownTotal.current ?? 0;
    localStorage.setItem(LAST_SEEN_KEY, String(total));
    setUnreadCount(0);
  }, []);

  const deleteQuote = useCallback(async (id) => {
    try {
      await adminAxios().delete(`/admin/quotes/${id}`);
      setQuotes((prev) => prev.filter((q) => q.id !== id));
      toast.success("Devis supprimé.");
    } catch {
      toast.error("Erreur lors de la suppression.");
    }
  }, []);

  const deleteContact = useCallback(async (id) => {
    try {
      await adminAxios().delete(`/admin/contacts/${id}`);
      setContacts((prev) => prev.filter((c) => c.id !== id));
      toast.success("Contact supprimé.");
    } catch {
      toast.error("Erreur lors de la suppression.");
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await refreshAll();
      setLoading(false);
    };
    load();
  }, [refreshAll]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const s = await adminAxios().get("/admin/stats");
        const newTotal = (s.data.quotes_total || 0) + (s.data.contacts_total || 0);
        if (lastKnownTotal.current !== null && newTotal > lastKnownTotal.current) {
          await refreshAll();
        }
        setStats(s.data);
      } catch {
        // polling silencieux
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [refreshAll]);

  return (
    <AdminContext.Provider value={{
      stats, quotes, contacts, chats, chatbotStats,
      unreadCount, loading,
      refreshAll, updateQuoteStatus, markAllRead, deleteQuote, deleteContact,
    }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error("useAdmin must be used within AdminProvider");
  return ctx;
};
